import { isEmpty } from 'lodash';

import { STYLE_COMMANDS } from '../../../../common/richText/richTextConstants';
import {
    addHotTableColsLeft,
    addHotTableColsRight,
    addHotTableRowsAbove,
    addHotTableRowsBelow,
} from '../utils/hotInstanceUtils/grid/hotTableGridOperationsUtils';
import {
    addHotTableRowOrColumn,
    clearSelectedCells,
    jumpToCaptionFromLastCell,
    jumpToTitleFromFirstCell,
    removeHotTableRowOrColumn,
} from '../utils/hotInstanceUtils/grid/hotTableGridShortcutsUtils';
import { updateHotTableSelectedCellTextStyle } from '../utils/hotInstanceUtils/cell/hotTableCellUpdateUtils';
import { MilanoteHotPlugin } from './MilanoteHotPlugin';
import { TableDataVersion } from '../../../../common/table/TableTypes';
import { hasNoModifierKeys } from '../../../utils/keyboard/keyboardUtility';

export const GRID_SHORTCUTS_PLUGIN_NAME = 'GridShortcuts';

const SHORTCUTS_GROUP_HOT_GRID_DEFAULT = 'gridDefault';
const SHORTCUTS_GROUP_MN_GRID_FORMATTING = 'milanoteShortcuts.grid.formatting';
const SHORTCUTS_GROUP_MN_GRID_UPDATE = 'milanoteShortcuts.grid.update';

/**
 * Registers Milanote-specific grid shortcuts for tables.
 */
export default class GridShortcutsHotPlugin extends MilanoteHotPlugin {
    private gridShortcutsRegistered = false;

    static get PLUGIN_KEY(): string {
        return GRID_SHORTCUTS_PLUGIN_NAME;
    }

    enablePlugin(): void {
        this.addHook('afterInit', () => this.registerShortcuts());
        this.addHook('afterDocumentKeyDown', (event) => this.onAfterDocumentKeyDown(event));

        super.enablePlugin();
    }

    disablePlugin(): void {
        this.unregisterShortcuts();

        super.disablePlugin();
    }

    /**
     * Prevent Handsontable shortcut events from bubbling up to Milanote's canvas shortcuts.
     */
    onAfterDocumentKeyDown(event: KeyboardEvent): void {
        const isGridContextActive = this.milanoteHot.getShortcutManager().getActiveContextName() === 'grid';
        if (!isGridContextActive) return;

        const cellSelections = this.milanoteHot.getSelected();
        if (isEmpty(cellSelections)) return;

        // TODO-TABLES: This fixes a Tiptap-only bug but breaks Draft; remove the `if` when Draft is removed.
        if (this.milanoteHot.milanoteProps.version === TableDataVersion.V3) {
            // Prevent Handsontable cell selection typing from triggering global shortcuts (eg 'z' for zoom).
            const isTypingKey = event.key.length === 1 && hasNoModifierKeys(event);
            if (isTypingKey) event.preventDefault();
        }

        event.stopPropagation();
    }

    /**
     * Registers grid-context shortcuts that should be available for the lifetime of the table instance.
     */
    registerShortcuts(): void {
        if (this.gridShortcutsRegistered) return;

        const shortcutManager = this.milanoteHot.getShortcutManager();
        const gridContext = shortcutManager.getContext('grid');

        if (!gridContext) return;

        gridContext.removeShortcutsByKeys(['Control/Meta', 'Enter']);
        gridContext.removeShortcutsByKeys(['Backspace']);
        gridContext.removeShortcutsByKeys(['Delete']);

        const shouldRunGridShortcut = () =>
            !this.milanoteHot.getActiveEditor()?.isOpened() && !isEmpty(this.milanoteHot.getSelected());

        // Register grid update and navigation shortcuts before Handsontable's default grid shortcuts.
        const gridUpdateShortcutOptions = {
            group: SHORTCUTS_GROUP_MN_GRID_UPDATE,
            relativeToGroup: SHORTCUTS_GROUP_HOT_GRID_DEFAULT,
            position: 'before' as 'before' | 'after',
            runOnlyIf: shouldRunGridShortcut,
        };

        gridContext.addShortcuts([
            {
                // Add a row below, or add a column to the right when the ref-column header is selected.
                keys: [['Control/Meta', 'Enter']],
                callback: () => addHotTableRowOrColumn(this.milanoteHot),
                preventDefault: true,
                stopPropagation: true,
                ...gridUpdateShortcutOptions,
            },
            {
                // Remove selected rows, or remove columns when the ref-column header is selected.
                keys: [['Control/Meta', 'Backspace']],
                callback: () => removeHotTableRowOrColumn(this.milanoteHot),
                preventDefault: true,
                stopPropagation: true,
                ...gridUpdateShortcutOptions,
            },
            {
                // Jump focus out to the caption when tabbing forward from the bottom-right cell.
                keys: [['Tab']],
                callback: () =>
                    jumpToCaptionFromLastCell(this.milanoteHot, this.milanoteHot.milanoteProps.jumpToCaption),
                preventDefault: true,
                ...gridUpdateShortcutOptions,
            },
            {
                // Jump focus out to the title when reverse-tabbing from the top-left cell.
                keys: [['Shift', 'Tab']],
                callback: () => jumpToTitleFromFirstCell(this.milanoteHot, this.milanoteHot.milanoteProps.jumpToTitle),
                preventDefault: true,
                ...gridUpdateShortcutOptions,
            },
            {
                // Clear selected cells even if they were temporarily marked read-only during table interactions.
                keys: [['Backspace'], ['Delete'], ['Shift', 'Backspace']],
                callback: () => clearSelectedCells(this.milanoteHot),
                ...gridUpdateShortcutOptions,
            },
            {
                // Insert a row above the current selection.
                keys: [['Alt', 'ArrowUp']],
                callback: () => addHotTableRowsAbove(this.milanoteHot, undefined),
                ...gridUpdateShortcutOptions,
            },
            {
                // Insert a row below the current selection.
                keys: [['Alt', 'ArrowDown']],
                callback: () => addHotTableRowsBelow(this.milanoteHot, undefined),
                ...gridUpdateShortcutOptions,
            },
            {
                // Insert a column to the left of the current selection.
                keys: [['Alt', 'ArrowLeft']],
                callback: () => addHotTableColsLeft(this.milanoteHot, undefined),
                ...gridUpdateShortcutOptions,
            },
            {
                // Insert a column to the right of the current selection.
                keys: [['Alt', 'ArrowRight']],
                callback: () => addHotTableColsRight(this.milanoteHot, undefined),
                ...gridUpdateShortcutOptions,
            },
            {
                // Keep table-selection arrow navigation from bubbling into canvas movement shortcuts.
                keys: [
                    ['ArrowUp'],
                    ['ArrowRight'],
                    ['ArrowDown'],
                    ['ArrowLeft'],
                    ['Shift', 'ArrowUp'],
                    ['Shift', 'ArrowRight'],
                    ['Shift', 'ArrowDown'],
                    ['Shift', 'ArrowLeft'],
                ],
                callback: () => {},
                ...gridUpdateShortcutOptions,
                stopPropagation: true,
            },
        ]);

        // Register whole-cell formatting shortcuts separately for independent teardown.
        const gridFormattingShortcutOptions = {
            group: SHORTCUTS_GROUP_MN_GRID_FORMATTING,
            runOnlyIf: shouldRunGridShortcut,
        };

        gridContext.addShortcuts([
            {
                // Mirror Milanote rich-text bold formatting for the current cell selection.
                keys: [['Control/Meta', 'b']],
                callback: () => updateHotTableSelectedCellTextStyle(this.milanoteHot, STYLE_COMMANDS.BOLD),
                ...gridFormattingShortcutOptions,
            },
            {
                // Mirror Milanote rich-text italic formatting for the current cell selection.
                keys: [['Control/Meta', 'i']],
                callback: () => updateHotTableSelectedCellTextStyle(this.milanoteHot, STYLE_COMMANDS.ITALIC),
                ...gridFormattingShortcutOptions,
            },
            {
                // Mirror Milanote rich-text strikethrough formatting for the current cell selection.
                keys: [['Control/Meta', 'shift', 'x']],
                callback: () => updateHotTableSelectedCellTextStyle(this.milanoteHot, STYLE_COMMANDS.STRIKETHROUGH),
                ...gridFormattingShortcutOptions,
            },
        ]);

        this.gridShortcutsRegistered = true;
    }

    /**
     * Removes Milanote grid shortcut groups when the table instance is torn down.
     */
    unregisterShortcuts(): void {
        if (!this.gridShortcutsRegistered) return;

        const shortcutManager = this.milanoteHot.getShortcutManager();
        const gridContext = shortcutManager.getContext('grid');

        if (!gridContext) return;

        gridContext.removeShortcutsByGroup(SHORTCUTS_GROUP_MN_GRID_FORMATTING);
        gridContext.removeShortcutsByGroup(SHORTCUTS_GROUP_MN_GRID_UPDATE);

        this.gridShortcutsRegistered = false;
    }
}
